import { Editor } from '@tiptap/react';
import React, { useEffect } from 'react';
import { useNotify } from 'react-admin';
import { EditorialAssetSearchResultItem } from '../../../../../../utility/types/dataProvider';
import { useAssetEditorsContext } from '../../../context/AssetEditorsContextProvider';
import { HandleMediaLibraryInsertionArgs } from '../../collection/types';
import { createBloxInlineNode } from '../../article/InlineAssets/extensions/BloxInline';
import { useMediaLibStore } from './useMediaLibStore';

/**
 * Handles the insertion of bloxInline nodes into the article, considering drop events or a specific replace node.
 *
 * @param editor The Tiptap editor instance.
 * @param insertData Data about the assets to be inserted.
 * @param notify Function to trigger notifications.
 * @param dropEvent Optional; The browser's drop event, containing position data.
 * @param replaceNode Optional; specifies a node position to replace content at, overriding the drop position.
 */
export const handleArticleInsertion = (
  editor: Editor | null,
  insertData: EditorialAssetSearchResultItem[],
  notify: ReturnType<typeof useNotify>,
  dropEvent?: React.DragEvent<Element>,
  replaceNode?: number | undefined,
) => {
  if (!editor || !editor.schema.nodes.bloxInline) {
    console.error(
      '[handleArticleInsertion] Editor or bloxInline node type is not available in the editor schema.',
    );
    notify('Sorry, there was an error with the editor.', {
      type: 'error',
      autoHideDuration: 7000,
    });
    return false;
  }

  // Set the initial position to the replaceNode, if it was specified.
  let position = replaceNode;

  // If replaceNode was specified, delete the node at that position.
  if (replaceNode !== undefined) {
    editor?.chain().focus().setNodeSelection(replaceNode).deleteSelection().run();
  }

  // If no replaceNode is set, try to get the position from the drop event.
  if (position === undefined && dropEvent) {
    const coords = { left: dropEvent.clientX, top: dropEvent.clientY };
    const posAtCoords = editor.view.posAtCoords(coords);
    position = posAtCoords?.pos;
  }

  // If no position is set yet, use the current cursor position.
  if (position === undefined) {
    position = editor.state.selection.anchor;
  }

  let wasSuccessful = editor
    .chain()
    .focus()
    .command(({ tr, state }) => {
      // Ensure the final position is a valid number before proceeding.
      if (position === undefined) {
        console.error('Invalid position for insertion:', position);
        notify('Invalid position for insertion.', {
          type: 'error',
          autoHideDuration: 7000,
        });
        return false;
      }

      // Insert the new bloxInline nodes at the final position.

      insertData.forEach((record) => {
        const node = createBloxInlineNode(editor, record);
        // Check if the node was created successfully before inserting it.
        if (!node || !node.nodeSize || typeof position !== 'number') {
          console.error('Failed to create node for insertion, returning...');
          return false;
        }
        tr.insert(position, node);
        position += node.nodeSize;
      });

      // Return tr.docChanged state.
      return tr.docChanged;
    })
    .focus()
    .run();

  if (wasSuccessful) {
    notify(`Added ${insertData.length} items inline.`, {
      type: 'success',
      autoHideDuration: 7000,
    });
  } else {
    notify('Failed to add items.', { type: 'error', autoHideDuration: 7000 });
  }

  return wasSuccessful;
};
/**
 * useSetMediaLibraryInsertForArticles - onClick setup
 * for media library with a useEffect
 * which sets the media lib's zustand store functon `onClickInsert`.
 * Hook depends on useAssetEditorsContext and useMediaLibStore.
 * We can modify this later on to accept a specific editor as a parameter.
 */
export const useSetMediaLibraryInsertForArticles = () => {
  const notify = useNotify();
  const { contentEditor } = useAssetEditorsContext();

  const {
    selectedRecords,
    setSelectedRecords,
    setOnClickInsert,
    replaceNode,
    setReplaceNode,
  } = useMediaLibStore();

  useEffect(() => {
    const handleInsertForArticle = (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      event.stopPropagation();

      const insertSuccessful = handleArticleInsertion(
        contentEditor,
        selectedRecords,
        notify,
        undefined, // No drag event here, since this is the onClick handler setup for MediaLib.
        replaceNode,
      );

      if (insertSuccessful) {
        setSelectedRecords([]); // Clear selected records after successful insertion
        setReplaceNode(undefined);
      }
    };

    setOnClickInsert(handleInsertForArticle);

    return () => {
      setOnClickInsert(undefined); // Ensure cleanup on unmount
    };
  }, [
    contentEditor,
    notify,
    selectedRecords,
    setSelectedRecords,
    setOnClickInsert,
    replaceNode,
    setReplaceNode,
  ]);
};

/**
 * useSetMediaLibraryInsertForCollections
 * Sets up the link between the CollectionAssetEditorContextProvider and
 * the Media Library Drawer. This hook sets the onClick for the Media Library
 * and updates the state of the CollectionAssetEditorContextProvider using a
 * function provided to it (handleMediaLibraryInsertion).
 */

export interface UseSetMediaLibraryInsertForCollectionsParams {
  mainRecordUUID?: string;
  handleMediaLibraryInsertion: (args: HandleMediaLibraryInsertionArgs) => void;
}

export const useSetMediaLibraryInsertForCollections = ({
  mainRecordUUID,
  handleMediaLibraryInsertion,
}: UseSetMediaLibraryInsertForCollectionsParams) => {
  const { selectedRecords: mediaLibSelectedRecords } = useMediaLibStore();
  const { setOnClickInsert } = useMediaLibStore();

  useEffect(() => {
    const handleInsertForCollection = (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      event.stopPropagation();
      handleMediaLibraryInsertion({ records: mediaLibSelectedRecords, mainRecordUUID });
    };

    // Set the onClickInsert function for the media library
    setOnClickInsert(handleInsertForCollection);

    // Cleanup on unmount
    return () => setOnClickInsert(undefined);
  }, [
    handleMediaLibraryInsertion,
    setOnClickInsert,
    mediaLibSelectedRecords,
    mainRecordUUID,
  ]);
};
